import React, { useEffect, useMemo, useState } from "react";
import { partnerSidebarItems } from "./constants/sidebarItems";
import { groupDisplayOrder } from "./constants/sidebarMapping";
import { Dialog } from "primereact/dialog";
import { Accordion, AccordionTab } from "primereact/accordion";
import { Button, Modal } from "../../../../components/core";
import { useSelector } from "react-redux";
import { Strings } from "../../../../constants/strings";

const PERMISSION_LEVELS = [
  { id: "0", label: "No Access" },
  { id: "1", label: "Read Only" },
  { id: "2", label: "Read & Write" },
  { id: "5", label: "Read, Write, & Approve / Reject" },
];

const PartnerPermissionDisplayDialog = ({
  visible,
  accountAccessPermissions,
  onHide,
  handleOnChange,
  onSavePermissions,
}) => {
  const [permissions, setPermissions] = useState({});
  const [groupPermissions, setGroupPermissions] = useState({});
  const [originalPermissions, setOriginalPermissions] = useState({});
  const [pendingGroupChange, setPendingGroupChange] = useState(null);

  const [validation, setValidation] = useState({
    icon: `${process.env.PUBLIC_URL}/assets/images/error.png`,
    title: "",
    message: "",
    open: false,
  });

  //   utils
  const accessPermission = useSelector(
    (state) => state.accessPermissions.accessPermissions
  );

  // Helper function to get permission label
  const getPermissionLabel = (permissionId) => {
    return (
      PERMISSION_LEVELS.find((p) => p.id === permissionId.toString())?.label ||
      "No Access"
    );
  };

  const getParentPermissionLevel = (pageTag) => {
    if (!accessPermission || !accessPermission[pageTag]) {
      return 0; // No access by default
    }
    return parseInt(accessPermission[pageTag]);
  };

  // Check if a page is new (doesn't have existing permissions in parent)
  const isNewPage = (pageTag) => {
    return !accessPermission || accessPermission[pageTag] === undefined;
  };

  // Validate if trying to assign higher permission than parent has on existing pages
  const validatePermissionLevel = (
    pageTag,
    newPermissionLevel,
    isLocked = false
  ) => {
    // Skip validation for locked pages (they have fixed permissions)
    if (isLocked) {
      return { isValid: true };
    }

    // Allow any permission for new pages
    if (isNewPage(pageTag)) {
      return { isValid: true };
    }

    const parentLevel = getParentPermissionLevel(pageTag);
    const requestedLevel = parseInt(newPermissionLevel);

    // For existing pages, check if trying to assign higher permission than parent has
    if (requestedLevel > parentLevel) {
      return {
        isValid: true, // Changed to true - we warn instead of blocking
        warning: true,
        message: `You are assigning a higher permission level than what you have. Your permission for this page is ${
          PERMISSION_LEVELS.find((p) => p.id === parentLevel.toString())
            ?.label || "No Access"
        }.`,
      };
    }

    return { isValid: true };
  };

  // Check if assigning higher permission than parent on existing page (needs warning)
  const checkPermissionWarning = (pageTag, newPermissionLevel) => {
    const parentLevel = getParentPermissionLevel(pageTag);
    const requestedLevel = parseInt(newPermissionLevel);

    // If trying to assign higher permission than parent has
    if (requestedLevel > parentLevel) {
      return {
        parentPermission: getPermissionLabel(parentLevel.toString()),
        requestedPermission: getPermissionLabel(newPermissionLevel),
      };
    }

    return null;
  };

  // Check the maximum permission allowed for a page based on parent access
  const getMaxAllowedPermission = (pageTag) => {
    return getParentPermissionLevel(pageTag);
  };

  const pages = useMemo(() => {
    const allPages = [];

    partnerSidebarItems.forEach((section) => {
      // Handle standalone pages (no content)
      if (!section.content) {
        allPages.push({
          name: section.title,
          group: section.title,
          groupNormalized: section.title.toLowerCase().replace(/\s+/g, "-"),
          tag: section.tag,
          isLocked: section.isLocked || false,
          defaultPermission: section.defaultPermission || null,
        });
      } else {
        // Handle pages with content
        section.content.forEach((page) => {
          allPages.push({
            name: page.title,
            group: section.title,
            groupNormalized: section.title.toLowerCase().replace(/\s+/g, "-"),
            tag: page.tag,
            isLocked: page.isLocked || false,
            defaultPermission: page.defaultPermission || null,
          });
        });
      }
    });

    return allPages;
  }, []);

  // Group pages by their section
  const groupedPages = useMemo(() => {
    const grouped = {};

    // Process ALL pages from sidebar
    pages.forEach((page) => {
      const normalizedGroup = page.group.toLowerCase().replace(/\s+/g, "-");
      if (!grouped[normalizedGroup]) {
        grouped[normalizedGroup] = {
          displayName: page.group,
          pages: [],
        };
      }

      // Add the page, whether it has a permission or not
      grouped[normalizedGroup].pages.push({
        ...page,
        // If no permission found, set as blank (or "0" for "No Access" default)
        isNewPage:
          originalPermissions[page.tag] === undefined && !page.isLocked,
      });
    });

    // Sort groups by display order
    const sorted = {};
    groupDisplayOrder.forEach((groupKey) => {
      if (grouped[groupKey]) {
        sorted[groupKey] = grouped[groupKey];
      }
    });

    return sorted;
  }, [pages, permissions]);

  useEffect(() => {
    const newGroupPermissions = {};

    Object.entries(groupedPages).forEach(([groupKey, groupData]) => {
      const pages = groupData.pages;

      // Get all permission values for this group
      const permissionValues = pages.map((page) => permissions[page.tag]);

      // Check if all pages have permissions and if they're all the same
      const allHavePermissions = permissionValues.every(
        (val) => val !== undefined && val !== null
      );
      const allSame =
        allHavePermissions &&
        permissionValues.every((val) => val === permissionValues[0]);

      if (!allHavePermissions) {
        // If not all have permissions, set to custom
        newGroupPermissions[groupKey] = "custom";
      } else if (allSame) {
        // If all are the same, use that permission level
        newGroupPermissions[groupKey] = permissionValues[0];
      } else {
        // If they differ, set to custom
        newGroupPermissions[groupKey] = "custom";
      }
    });

    setGroupPermissions(newGroupPermissions);
  }, [groupedPages, permissions]);

  useEffect(() => {
    if (accountAccessPermissions) {
      let parsed = accountAccessPermissions;
      if (typeof accountAccessPermissions === "string") {
        try {
          parsed = JSON.parse(accountAccessPermissions);
        } catch (e) {
          parsed = {};
        }
      }

      setOriginalPermissions(parsed);

      // ✅ Set default permissions for locked pages (always override)
      const withDefaults = { ...parsed };
      pages.forEach((page) => {
        if (page.isLocked && page.defaultPermission) {
          // Always use the default permission for locked pages
          withDefaults[page.tag] = page.defaultPermission;
        }
      });

      setPermissions(withDefaults);
    }
  }, [accountAccessPermissions, visible, pages]);

  // Separate function to apply the group permission change
  const applyGroupPermissionChange = (groupKey, permissionId) => {
    setGroupPermissions((prev) => ({
      ...prev,
      [groupKey]: permissionId,
    }));

    const groupPages = groupedPages[groupKey]?.pages || [];
    const newPermissions = { ...permissions };

    if (permissionId === "custom") {
      groupPages.forEach((page) => {
        if (!page.isLocked) {
          delete newPermissions[page.tag];
        }
      });
    } else {
      groupPages.forEach((page) => {
        if (!page.isLocked) {
          newPermissions[page.tag] = permissionId;
        }
      });
    }

    setPermissions(newPermissions);
  };

  const handleGroupPermissionChange = (groupKey, permissionId) => {
    // Don't validate for "custom" selection
    if (permissionId !== "custom") {
      const groupPages = groupedPages[groupKey]?.pages || [];

      // Check for pages with different current permissions
      const differentPermissions = [];
      // Check for pages where we're assigning higher permission than parent has
      const higherPermissionPages = [];

      groupPages.forEach((page) => {
        if (!page.isLocked) {
          if (permissions[page.tag] && permissions[page.tag] !== permissionId) {
            differentPermissions.push({
              name: page.name,
              current: getPermissionLabel(permissions[page.tag]),
              new: getPermissionLabel(permissionId),
            });
          }

          // Check if assigning higher permission than parent on existing page
          const warning = checkPermissionWarning(page.tag, permissionId);
          if (warning) {
            higherPermissionPages.push({
              name: page.name,
              ...warning,
            });
          }
        }
      });

      // If there are higher permission assignments, show warning dialog
      if (higherPermissionPages.length > 0) {
        setValidation({
          icon: `${process.env.PUBLIC_URL}/assets/images/warning.png`,
          title: "Permission Level Warning",
          message: (
            <div className="text-center font-montserrat w-full">
              <p className="mb-4">
                You are assigning permissions higher than what you currently
                have on the following pages:
              </p>
              <div className="mb-4 text-left bg-yellow-50 p-4 rounded max-h-48 overflow-y-auto border border-yellow-200">
                {higherPermissionPages.map((page, idx) => (
                  <div
                    key={idx}
                    className="mb-3 pb-3 border-b border-yellow-200 last:border-b-0"
                  >
                    <p className="font-semibold text-sm">{page.name}</p>
                    <div className="flex items-center justify-between text-sm mt-1">
                      <span className="text-gray-600">
                        Your current:{" "}
                        <span className="font-semibold">
                          {page.parentPermission}
                        </span>
                      </span>
                      <span className="mx-2">→</span>
                      <span className="text-red-600">
                        Assigning:{" "}
                        <span className="font-semibold">
                          {page.requestedPermission}
                        </span>
                      </span>
                    </div>
                  </div>
                ))}
              </div>
              <p className="text-sm text-gray-700">
                Do you want to proceed with these changes?
              </p>
            </div>
          ),
          open: true,
          actions: (
            <div className="grid grid-cols-2 gap-2">
              <Button
                onClick={() => {
                  setValidation({ open: false });
                  setPendingGroupChange(null);
                }}
                value={"Cancel"}
                className={"p-2"}
              />
              <Button
                onClick={() => {
                  // For group changes, user already saw warning for higher permissions, so allow the change
                  // Just apply the permission change directly
                  applyGroupPermissionChange(groupKey, permissionId);
                  setValidation({ open: false });
                  setPendingGroupChange(null);
                }}
                value={"Confirm"}
                className={"p-2"}
              />
            </div>
          ),
        });
        return;
      }

      // If there are permission differences (but no higher permission issues), show change confirmation
      if (differentPermissions.length > 0) {
        setValidation({
          icon: `${process.env.PUBLIC_URL}/assets/images/warning.png`,
          title: "Confirm Permission Changes",
          message: (
            <div className="text-center font-montserrat w-full">
              <p className="mb-4">
                You are about to change permissions for multiple pages. Here's
                what will change:
              </p>
              <div className="mb-4 text-left bg-gray-100 p-4 rounded max-h-48 overflow-y-auto">
                {differentPermissions.map((change, idx) => (
                  <div
                    key={idx}
                    className="mb-3 pb-3 border-b border-gray-300 last:border-b-0"
                  >
                    <p className="font-semibold text-sm">{change.name}</p>
                    <div className="flex items-center justify-between text-sm mt-1">
                      <span className="text-gray-600">
                        Current:{" "}
                        <span className="font-semibold">{change.current}</span>
                      </span>
                      <span className="mx-2">→</span>
                      <span className="text-blue-600">
                        New: <span className="font-semibold">{change.new}</span>
                      </span>
                    </div>
                  </div>
                ))}
              </div>
              <p className="text-sm text-gray-700">
                Do you want to proceed with these changes?
              </p>
            </div>
          ),
          open: true,
          actions: (
            <div className="grid grid-cols-2 gap-2">
              <Button
                onClick={() => {
                  setValidation({ open: false });
                  setPendingGroupChange(null);
                }}
                value={"Cancel"}
                className={"p-2"}
              />
              <Button
                onClick={() => {
                  // If validation passes, apply the change
                  applyGroupPermissionChange(groupKey, permissionId);
                  setValidation({ open: false });
                  setPendingGroupChange(null);
                }}
                value={"Confirm"}
                className={"p-2"}
              />
            </div>
          ),
        });
        return;
      }
    }

    // If no conflicts or changes, apply immediately
    applyGroupPermissionChange(groupKey, permissionId);
  };

  // Handle individual page permission change
  const handlePagePermissionChange = (
    pageTag,
    permissionId,
    isLocked = false
  ) => {
    // Skip validation for locked pages
    if (isLocked) {
      setPermissions((prev) => ({
        ...prev,
        [pageTag]: permissionId,
      }));
      return;
    }

    // For new pages, show confirmation that you're adding access where there was none
    if (isNewPage(pageTag)) {
      setValidation({
        icon: `${process.env.PUBLIC_URL}/assets/images/warning.png`,
        title: "New Page Access",
        message: (
          <div className="text-center font-montserrat">
            <p className="mb-4">
              This is a new page. You are about to grant access where there was
              previously none.
            </p>
            <div className="mb-4 text-left bg-blue-50 p-4 rounded border border-blue-200">
              <p className="text-sm mb-3">
                <strong>Your current permission:</strong>{" "}
                <span className="text-gray-600">No Access</span>
              </p>
              <p className="text-sm">
                <strong>Permission you're assigning:</strong>{" "}
                <span className="text-blue-600 font-semibold">
                  {getPermissionLabel(permissionId)}
                </span>
              </p>
            </div>
            <p className="text-sm text-gray-700">Do you want to proceed?</p>
          </div>
        ),
        open: true,
        actions: (
          <div className="grid grid-cols-2 gap-2">
            <Button
              onClick={() => setValidation({ open: false })}
              value={"Cancel"}
              className={"p-2"}
            />
            <Button
              onClick={() => {
                // For new pages, user already saw warning, so allow the change
                if (!isNewPage(pageTag)) {
                  // For existing pages, validate that requested permission doesn't exceed parent permissions
                  const maxAllowed = getMaxAllowedPermission(pageTag);
                  const requested = parseInt(permissionId);

                  if (requested > maxAllowed) {
                    setValidation({
                      icon: `${process.env.PUBLIC_URL}/assets/images/error.png`,
                      title: "Permission Denied",
                      message: (
                        <div className="text-center">
                          <p>
                            You cannot assign a higher permission level than
                            what you have. Your maximum allowed permission is{" "}
                            {getPermissionLabel(maxAllowed.toString())}.
                          </p>
                        </div>
                      ),
                      open: true,
                      actions: (
                        <div className="flex justify-center">
                          <Button
                            onClick={() => setValidation({ open: false })}
                            value={"OK"}
                            className={"p-2 w-32"}
                          />
                        </div>
                      ),
                    });
                    return;
                  }
                }

                setPermissions((prev) => ({
                  ...prev,
                  [pageTag]: permissionId,
                }));
                setValidation({ open: false });
              }}
              value={"Confirm"}
              className={"p-2"}
            />
          </div>
        ),
      });
      return;
    }

    // For existing pages, check if we're assigning higher permission than parent
    const warning = checkPermissionWarning(pageTag, permissionId);

    if (warning) {
      // Show warning but allow proceeding
      setValidation({
        icon: `${process.env.PUBLIC_URL}/assets/images/warning.png`,
        title: "Permission Level Warning",
        message: (
          <div className="text-center font-montserrat">
            <p className="mb-4">
              You are assigning a higher permission level than what you
              currently have.
            </p>
            <div className="mb-4 text-left bg-yellow-50 p-4 rounded border border-yellow-200">
              <p className="text-sm mb-3">
                <strong>Your current permission:</strong>{" "}
                {warning.parentPermission}
              </p>
              <p className="text-sm">
                <strong>Permission you're assigning:</strong>{" "}
                <span className="text-red-600">
                  {warning.requestedPermission}
                </span>
              </p>
            </div>
            <p className="text-sm text-gray-700">Do you want to proceed?</p>
          </div>
        ),
        open: true,
        actions: (
          <div className="grid grid-cols-2 gap-2">
            <Button
              onClick={() => setValidation({ open: false })}
              value={"Cancel"}
              className={"p-2"}
            />
            <Button
              onClick={() => {
                setPermissions((prev) => ({
                  ...prev,
                  [pageTag]: permissionId,
                }));
                setValidation({ open: false });
              }}
              value={"Confirm"}
              className={"p-2"}
            />
          </div>
        ),
      });
      return;
    }

    // No warning - assign permission directly
    setPermissions((prev) => ({
      ...prev,
      [pageTag]: permissionId,
    }));
  };

  // Validation checker for custom assign groups
  const validateCustomAssignGroups = () => {
    const errors = [];

    Object.entries(groupedPages).forEach(([groupKey, groupData]) => {
      // Check if this group is set to custom assign
      if (groupPermissions[groupKey] === "custom") {
        // Check each non-locked page in this group
        const missingPermissions = groupData.pages
          .filter((page) => !page.isLocked) // Only check non-locked pages
          .filter((page) => {
            const permission = permissions[page.tag];
            return (
              permission === undefined ||
              permission === null ||
              permission === ""
            );
          });

        if (missingPermissions.length > 0) {
          errors.push({
            group: groupData.displayName,
            pages: missingPermissions.map((p) => p.name),
          });
        }
      }
    });

    return errors;
  };

  return (
    <>
      <Dialog
        visible={visible}
        closable={
          accessPermission.partners === 1 || accessPermission.partners === "1"
        }
        onHide={onHide}
        draggable={false}
        resizable={false}
        className="w-[950px]"
        header={
          <div className="text-secondary-purpleGray">
            Partner Access Permission Configuration
          </div>
        }
        footer={
          <>
            <div
              className={`${
                accessPermission.partners === 1 ||
                accessPermission.partners === "1"
                  ? "hidden"
                  : "w-full flex justify-center gap-2 py-4"
              }`}
            >
              <Button
                value={"Cancel"}
                className={"p-2"}
                onClick={() => {
                  setValidation({
                    icon: `${process.env.PUBLIC_URL}/assets/images/error.png`,
                    title: Strings.popup_confirm_cancel_title,
                    message: Strings.popup_confirm_cancel_massage,
                    open: true,
                    actions: (
                      <div className="grid grid-cols-2 gap-2">
                        <Button
                          onClick={() => setValidation({ open: false })}
                          value={"No"}
                          className={"p-2"}
                        />
                        <Button
                          onClick={() => {
                            // navigate(`/roles-and-templates/`);
                            onHide();
                            setValidation({ open: false });
                          }}
                          value={"Yes"}
                          className={"p-2"}
                        />
                      </div>
                    ),
                  });
                }}
              />
              <Button
                value={"Save Changes"}
                className={"p-2"}
                // disabled={!changesMade}
                onClick={() => {
                  // Validate custom assign groups before saving
                  const validationErrors = validateCustomAssignGroups();

                  if (validationErrors.length > 0) {
                    // Show validation error modal with HTML formatting
                    const errorMessage = (
                      <div className="text-center font-montserrat">
                        <p className="mb-3">
                          The following pages in custom assign groups do not
                          have permissions set:
                        </p>
                        <div className="mb-3 space-y-2">
                          {validationErrors.map((error, idx) => (
                            <div key={idx} className="pl-2">
                              <strong>{error.group}:</strong>
                              <div className="pl-4 text-sm">
                                {error.pages.join(", ")}
                              </div>
                            </div>
                          ))}
                        </div>
                        <p className="font-montserrat">
                          Please assign permissions to all pages before saving.
                        </p>
                      </div>
                    );

                    setValidation({
                      icon: `${process.env.PUBLIC_URL}/assets/images/error.png`,
                      title: "Incomplete Permissions",
                      message: errorMessage,
                      open: true,
                      actions: (
                        <div className="flex justify-center">
                          <Button
                            onClick={() => setValidation({ open: false })}
                            value={"OK"}
                            className={"p-2 w-32"}
                          />
                        </div>
                      ),
                    });
                    return;
                  }

                  // If validation passes, show save confirmation
                  setValidation({
                    icon: `${process.env.PUBLIC_URL}/assets/images/check.png`,
                    title: Strings.popup_confirm_save_title,
                    message: Strings.popup_confirm_save_massage,
                    open: true,
                    actions: (
                      <div className="grid grid-cols-2 gap-2">
                        <Button
                          onClick={() => setValidation({ open: false })}
                          value={"No"}
                          className={"p-2 w-full"}
                        />
                        {/* pass the access permission to the parent */}
                        <Button
                          onClick={() => {
                            setValidation({ open: false });

                            onSavePermissions(permissions);
                            onHide();
                          }}
                          value={"Yes"}
                          className={"p-2 w-full"}
                        />
                      </div>
                    ),
                  });
                }}
              />
            </div>
          </>
        }
      >
        <div className="w-full max-w-6xl mx-auto">
          <Accordion multiple activeIndex={[0]}>
            {Object.entries(groupedPages).map(
              ([groupKey, groupData], index) => (
                <AccordionTab
                  key={groupKey}
                  headerClassName="w-full"
                  header={groupData.displayName
                    ?.toUpperCase()
                    .replaceAll("-", " ")}
                >
                  <div className="space-y-4">
                    {/* Group-level permission options */}
                    {(() => {
                      // ✅ Check if all pages in this group are locked
                      const allPagesLocked = groupData.pages.every(
                        (p) => p.isLocked
                      );

                      return (
                        <div className="space-y-2 pb-4 border-b border-gray-300">
                          <div className="space-y-2">
                            <label
                              className={`flex items-center gap-3 ${
                                allPagesLocked
                                  ? "cursor-not-allowed opacity-50"
                                  : "cursor-pointer"
                              }`}
                            >
                              <input
                                type="radio"
                                name={`group-${groupKey}`}
                                value="0"
                                checked={groupPermissions[groupKey] === "0"}
                                onChange={(e) =>
                                  handleGroupPermissionChange(
                                    groupKey,
                                    e.target.value
                                  )
                                }
                                className="w-4 h-4"
                                disabled={allPagesLocked}
                              />
                              <span>Set all to no access</span>
                            </label>

                            <label
                              className={`flex items-center gap-3 ${
                                allPagesLocked
                                  ? "cursor-not-allowed opacity-50"
                                  : "cursor-pointer"
                              }`}
                            >
                              <input
                                type="radio"
                                name={`group-${groupKey}`}
                                value="1"
                                checked={groupPermissions[groupKey] === "1"}
                                onChange={(e) =>
                                  handleGroupPermissionChange(
                                    groupKey,
                                    e.target.value
                                  )
                                }
                                className="w-4 h-4"
                                disabled={allPagesLocked}
                              />
                              <span>Set all to read only</span>
                            </label>

                            <label
                              className={`flex items-center gap-3 ${
                                allPagesLocked
                                  ? "cursor-not-allowed opacity-50"
                                  : "cursor-pointer"
                              }`}
                            >
                              <input
                                type="radio"
                                name={`group-${groupKey}`}
                                value="2"
                                checked={groupPermissions[groupKey] === "2"}
                                onChange={(e) =>
                                  handleGroupPermissionChange(
                                    groupKey,
                                    e.target.value
                                  )
                                }
                                className="w-4 h-4"
                                disabled={allPagesLocked}
                              />
                              <span>Set all to read and write</span>
                            </label>

                            <label
                              className={`flex items-center gap-3 ${
                                allPagesLocked
                                  ? "cursor-not-allowed opacity-50"
                                  : "cursor-pointer"
                              }`}
                            >
                              <input
                                type="radio"
                                name={`group-${groupKey}`}
                                value="5"
                                checked={groupPermissions[groupKey] === "5"}
                                onChange={(e) =>
                                  handleGroupPermissionChange(
                                    groupKey,
                                    e.target.value
                                  )
                                }
                                className="w-4 h-4"
                                disabled={allPagesLocked}
                              />
                              <span>
                                Set all to read, write, and approve/reject
                              </span>
                            </label>

                            <label
                              className={`flex items-center gap-3 ${
                                allPagesLocked
                                  ? "cursor-not-allowed opacity-50"
                                  : "cursor-pointer"
                              }`}
                            >
                              <input
                                type="radio"
                                name={`group-${groupKey}`}
                                value="custom"
                                checked={
                                  groupPermissions[groupKey] === "custom"
                                }
                                onChange={(e) =>
                                  handleGroupPermissionChange(
                                    groupKey,
                                    e.target.value
                                  )
                                }
                                className="w-4 h-4"
                                disabled={allPagesLocked}
                              />
                              <span>Custom assign</span>
                            </label>
                          </div>
                        </div>
                      );
                    })()}

                    {/* Individual page permissions table */}
                    <table className="w-full items-center border-collapse border border-gray-400">
                      <tbody>
                        {groupData.pages.map((page) => (
                          <tr key={page.tag} className="w-full">
                            <td className="p-2 border border-gray-400 w-2/4">
                              <p>{page.name}</p>
                              {page.isLocked && (
                                <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded font-semibold">
                                  <i className="pi pi-lock mr-1"></i>
                                  Auto-granted
                                </span>
                              )}
                              {page.isNewPage && (
                                <span className="text-xs bg-yellow-200 text-yellow-800 px-2 py-1 rounded font-semibold">
                                  New
                                </span>
                              )}
                            </td>
                            <td className="p-2 border border-gray-400">
                              <div className="flex justify-center gap-4">
                                {PERMISSION_LEVELS.map((level) => {
                                  return (
                                    <label
                                      key={level.id}
                                      className={`flex items-center gap-2 ${
                                        page.isLocked
                                          ? "cursor-not-allowed opacity-50"
                                          : "cursor-pointer"
                                      }`}
                                    >
                                      <input
                                        type="radio"
                                        name={`page-${page.tag}`}
                                        value={level.id}
                                        checked={
                                          parseInt(permissions[page.tag]) ===
                                          parseInt(level.id)
                                        }
                                        onChange={(e) =>
                                          handlePagePermissionChange(
                                            page.tag,
                                            e.target.value
                                          )
                                        }
                                        disabled={page.isLocked}
                                        className="w-4 h-4"
                                      />
                                      <span className="text-sm">
                                        {level.label}
                                      </span>
                                    </label>
                                  );
                                })}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </AccordionTab>
              )
            )}
          </Accordion>
        </div>
      </Dialog>

      <Modal
        icon={validation.icon}
        title={validation.title}
        message={validation.message}
        open={validation.open}
        actions={validation.actions}
      />
    </>
  );
};

export default PartnerPermissionDisplayDialog;
